import Loading from 'app/atoms/Loading';
import { Suspense, useEffect } from 'react';
import '../../pages/CityGuide/CityGuide.scss';
import PartnerWithUsStep from './PartnerWithUsStep/PartnerWithUsStep';
import { Col, Form, InputGroup } from 'react-bootstrap';
import GoogleAutocompleteControlled from 'app/atoms/GoogleAutocompleteControlled/GoogleAutocompleteControlled';
import { FormProvider, useForm } from 'react-hook-form';
import PhoneInputControlled from 'app/atoms/PhoneInputControlled/PhoneInputControlled';
import { CountryInfo } from '../../../redux/countryInfo/countryInfo';
import HowDidYouHear from './HowDidYouHear/HowDidYouHear';
import PropertyAdditionalInformation from './PropertyAdditionalInformation/PropertyAdditionalInformation';
import AdditionalInformationHeroSection from './AdditionalInformationHeroSection/AdditionalInformationHeroSection';
import AdditionalInformationTitle from './AdditionalInformationTitle/AdditionalInformationTitle';
import { useLocation } from 'react-router-dom';
import { connect } from 'react-redux';

const steps = [
  {
    label: 'step 1',
    content:
      "Fill out the Urbain Owner Form. If your property is in a location and of a type we can help with, you'll be prompted to provide some additional details. If your property has a profile on a listing site, such as AirBnb, please include a link."
  },
  {
    label: 'step 2',
    content: 'We’ll assess your home, and call you to discuss the next steps, and what you can expect as an Urbain owner.'
  },
  {
    label: 'step 3',
    content:
      'We’ll conduct a virtual walk-through of your property, and take you through our quality control, health, safety, and security policies and procedures. Once COVID regulations allow, we’ll set up a visit from one of our local team members, and a professional cleaner, to prepare your property for rental and get you started as an Urbain owner!'
  }
];

interface AdditionalInformationProps {
  countryInfo: CountryInfo;
}

export interface AdditionalInformationState {
  FirstName: string;
  LastName: string;
  EmailAddress: string;
  PhoneNumber: string;
  PropertyCountryName: string;
  PropertyPostalCode: string;
  PropertyNeighborhood: string;
  PropertySleepingCapacity: number;
  PropertyBedrooms: number;
  PropertyBathrooms: number;
  PropertyExtendedAddress: string;
  UUID: string;
  UploadBucket: string;
  UploadPrefix: string;
  UploadRegion: string;
  UploadKey: string;
  UploadSecret: string;
  UploadS3URL: string;
  PropertyLocality?: string;
  PropertyRegion?: string;
  PropertyStreetAddress?: string;
  PropertyStreetNumber?: string;
}

function AdditionalInformation({ countryInfo }: AdditionalInformationProps) {
  const { state } = useLocation<AdditionalInformationState>();

  const methods = useForm({
    defaultValues: {
      FirstName: state.FirstName,
      LastName: state.LastName,
      EmailAddress: state.EmailAddress,
      PhoneNumber: state.PhoneNumber,
      PropertyCountryName: state?.PropertyCountryName,
      PropertyPostalCode: state?.PropertyPostalCode,
      PropertyNeighborhood: state?.PropertyNeighborhood,
      PropertySleepingCapacity: state?.PropertySleepingCapacity,
      PropertyBedrooms: state?.PropertyBedrooms,
      PropertyBathrooms: state?.PropertyBathrooms,
      PropertyExtendedAddress: state?.PropertyExtendedAddress,
      PropertyLocality: state.PropertyLocality,
      PropertyRegion: state.PropertyRegion,
      PropertyStreetAddress: state.PropertyStreetAddress,
      PropertyStreetNumber: state.PropertyStreetNumber,
      UUID: state?.UUID,
      UploadBucket: state?.UploadBucket,
      UploadPrefix: state?.UploadPrefix,
      UploadRegion: state?.UploadRegion,
      UploadKey: state?.UploadKey,
      UploadSecret: state?.UploadSecret
    }
  });
  const {
    control,
    formState: { errors },
    watch
  } = methods;

  let formState = watch();

  useEffect(() => {
    console.log('formState: ', formState);
  }, [formState]);

  return (
    <Suspense
      fallback={
        <div>
          <Loading force={true} />
        </div>
      }
    >
      <FormProvider {...methods}>
        <AdditionalInformationHeroSection />
        <div className="d-flex flex-column align-items-center">
          <div className="city-guide-container col-12 col-sm-8">
            <AdditionalInformationTitle
              title={'Thank you for your interest!'}
              subHeader={'A little more information will help us prepare a personalized consultation for you.'}
            />
            <div className="partner-with-us-steps">
              {steps.map((step, index) => (
                <PartnerWithUsStep key={index} label={step.label} content={step.content} />
              ))}
            </div>
          </div>
          <div className="city-guide-container col-12 col-sm-8">
            <Form.Row>
              <Form.Group as={Col} controlId="formGridAddress" xs={12}>
                <Form.Label className="form-label-info required">Your Home Address </Form.Label>
                <GoogleAutocompleteControlled
                  region={{ region: 'GB' }}
                  selectProps={{
                    styles: {
                      container: (provided: any) => ({
                        ...provided,
                        flexGrow: 1
                      })
                    }
                  }}
                  streetNumberFormField={{ name: 'StreetNumber' }}
                  addresFormField={{ name: 'StreetAddress' }}
                  extendedAddresFormField={{ name: 'ExtendedAddress' }}
                  localityFormField={{ name: 'Locality' }}
                  regionFormField={{ name: 'Region' }}
                  postalCodeFormField={{ name: 'PostalCode' }}
                  countryFormField={{ name: 'CountryName' }}
                />
              </Form.Group>
            </Form.Row>
            <Form.Group className={'col-12 px-0'} as={Col} controlId="formSecondaryPhoneNumber">
              <Form.Label className="form-label-info required">Secondary Phone Number (optional)</Form.Label>
              <InputGroup className="mb-3 phone-input">
                <PhoneInputControlled control={control} name={'SecondaryPhoneNumber'} defaultCountry={countryInfo?.country_code?.toLowerCase()} />
              </InputGroup>
            </Form.Group>
          </div>
          <HowDidYouHear />
          <PropertyAdditionalInformation
            UUID={state?.UUID}
            UploadBucket={state?.UploadBucket}
            UploadPrefix={state?.UploadPrefix}
            UploadRegion={state?.UploadRegion}
            UploadKey={state?.UploadKey}
            UploadSecret={state?.UploadSecret}
            UploadS3URL={state?.UploadS3URL}
            PropertyExtendedAddress={state?.PropertyExtendedAddress}
          />
        </div>
      </FormProvider>
    </Suspense>
  );
}

const mapStateToProps = (state: any) => ({
  countryInfo: state.countryInfo
});

export default connect(mapStateToProps)(AdditionalInformation);
